import React from 'react';
import styled from '@emotion/styled';

const Wrapper = styled.div<{ size: number }>(({ size }) => ({
  position: 'relative',
  width: `${size}px`,
  height: `${size}px`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const SvgContainer = styled.svg(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  transform: 'rotate(-90deg)',
  pointerEvents: 'none',
}));

const TrackCircle = styled.circle(({ theme }) => ({
  stroke: theme.colors.neutrals.grey6,
  fill: 'none',
}));

const ProgressCircle = styled.circle(({ theme }) => ({
  stroke: theme.colors.brand.madlanGreen,
  fill: 'none',
  strokeLinecap: 'round',
  transition: 'stroke-dashoffset 0.3s ease-in-out',
}));

const InnerButton = styled.button(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: theme.borderRadius.round,
  backgroundColor: theme.colors.neutrals.white,
  border: 'none',
  outline: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
}));

export interface CircularProgressButtonProps {
  progress: number; // 0-100
}

export const CircularProgressButton: React.FC<CircularProgressButtonProps> = ({
  progress = 0,
  children,
}) => {
  const strokeWidth = 2;
  const size = 40;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const safeProgress = Math.min(Math.max(progress, 0), 100);
  const strokeDashoffset = circumference - ((safeProgress / 100) * circumference);

  return (
    <Wrapper size={size}>
      <SvgContainer viewBox={`0 0 ${size} ${size}`}>
        <TrackCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
        />
        <ProgressCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
        />
      </SvgContainer>
      <InnerButton type="button">
        {children}
      </InnerButton>
    </Wrapper>
  );
};
